import type { StructuredAddress } from "../addressValidation/types.js";
import { formatStructuredAddressMultiline } from "../addressValidation/addressFormat.js";

export type PlantLocationMatchInput = {
  name: string;
  location_code?: string | null;
  location_type?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  phone?: string | null;
  email?: string | null;
};

export type PlantMatchTarget = {
  plant_code: string;
  name: string;
};

export type PlantShipToFields = {
  plant_name: string;
  plant_address: string;
  plant_phone: string;
  plant_email: string;
};

function norm(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function scoreLocation(loc: PlantLocationMatchInput, plant: PlantMatchTarget): number {
  const locName = norm(loc.name);
  const locCode = norm(loc.location_code ?? "");
  const plantName = norm(plant.name);
  const plantCode = norm(plant.plant_code);
  if (!locName && !locCode) return 0;
  let score = loc.location_type === "plant" ? 10 : 0;
  if (plantCode && locCode && locCode === plantCode) score += 50;
  if (plantName && locName && locName === plantName) score += 40;
  if (plantCode && locName && locName === plantCode) score += 40;
  if (plantName && locCode && locCode === plantName) score += 40;
  if (plantCode && locCode && (locCode.includes(plantCode) || plantCode.includes(locCode))) {
    score += 15;
  }
  if (plantName && locName && (locName.includes(plantName) || plantName.includes(locName))) {
    score += 20;
  }
  if (plantCode && locName && (locName.includes(plantCode) || plantCode.includes(locName))) {
    score += 20;
  }
  if (plantName && locCode && (locCode.includes(plantName) || plantName.includes(locCode))) {
    score += 15;
  }
  return score;
}

export function pickPlantLocation(
  locations: readonly PlantLocationMatchInput[],
  plant: PlantMatchTarget
): PlantLocationMatchInput | null {
  let best: PlantLocationMatchInput | null = null;
  let bestScore = 0;
  for (const loc of locations) {
    const s = scoreLocation(loc, plant);
    if (s > bestScore) {
      best = loc;
      bestScore = s;
    }
  }
  if (best && bestScore >= 20) return best;
  const plantOnly = locations.filter((l) => l.location_type === "plant");
  if (plantOnly.length === 1) return plantOnly[0];
  return bestScore > 0 ? best : null;
}

export function formatLocationShipAddress(loc: PlantLocationMatchInput): string {
  const structured: StructuredAddress = {
    street: [loc.address_1, loc.address_2].filter((p) => (p ?? "").trim()).join(", ") || undefined,
    city: loc.city?.trim() || undefined,
    state: loc.state?.trim() || undefined,
    postal_code: loc.postal_code?.trim() || undefined,
  };
  return formatStructuredAddressMultiline(structured);
}

export function plantShipToFromLocation(
  plantName: string,
  loc: PlantLocationMatchInput | null
): PlantShipToFields {
  if (!loc) {
    return { plant_name: plantName, plant_address: "", plant_phone: "", plant_email: "" };
  }
  return {
    plant_name: plantName,
    plant_address: formatLocationShipAddress(loc),
    plant_phone: (loc.phone ?? "").trim(),
    plant_email: (loc.email ?? "").trim(),
  };
}

/** Lines printed in the PO PDF Ship-to box. */
export function formatPoShipToLines(args: {
  plantName?: string;
  plantAddress?: string;
  plantPhone?: string;
  plantEmail?: string;
  shipToOverride?: string;
}): string[] {
  const override = (args.shipToOverride ?? "").trim();
  const name = (args.plantName ?? "").trim();
  const raw = override
    ? name && !override.toLowerCase().includes(name.toLowerCase())
      ? [name, override]
      : [override]
    : [name, (args.plantAddress ?? "").trim(), (args.plantPhone ?? "").trim(), (args.plantEmail ?? "").trim()];
  return raw
    .flatMap((s) => s.split(/\n+/))
    .map((s) => s.trim())
    .filter(Boolean);
}
